import { useState } from "react";

import { Stack, Text } from "@similarweb/ui-components/foundations";

import { useToasts } from "app/common/hooks";
import { useTranslation } from "pro-features/components/WithTranslation";

import { mnpTrademarkNavigation } from "../../app/navigation";
import { useCreateTrademark, useGetTrademarks } from "../../services/api/trademarks";
import { trademarkAssetTracking } from "../../services/tracking/asset-management";
import { trademarksTracking } from "../../services/tracking/trademarks";
import type { ITradeMarkFormData } from "../../shared/components/forms/trademark-config-form/form-types";
import { TargetIcon } from "../../shared/components/forms/trademark-config-form/target-icon";
import { TrademarkConfigsForm } from "../../shared/components/forms/trademark-config-form/trademark-config-form";
import { trademarkConvertFromFormData } from "../../shared/components/forms/trademark-config-form/utils";
import { useSelectTrademark } from "../../shared/hooks/use-select-trademark";
import { Alert } from "../../shared/ui/alert";
import { Widget } from "../../shared/ui/layout/widget";
import { Toast } from "../../shared/ui/toast";

export const TrademarkConfigsCreatePage = () => {
    const toasts = useToasts();
    const translate = useTranslation();
    const [showAlert, setShowAlert] = useState(true);
    const { selectTrademarkAndRedirect } = useSelectTrademark();
    const { mutateAsync: createTrademark, isPending } = useCreateTrademark();
    const { refetch: refetchTrademarks, isFetching } = useGetTrademarks();

    const handleCloseTrademarkInfo = () => {
        setShowAlert(false);
    };

    const handleSaveConfig = async (newFormData: ITradeMarkFormData) => {
        try {
            const response = await createTrademark([trademarkConvertFromFormData(newFormData)]);
            trademarksTracking.onCreateTrademarkSave();
            trademarkAssetTracking.onCreated({
                id: response?.id,
                name: response?.name ?? newFormData.name,
                variationsCount: response?.variationsAndTypos?.length,
            });
            // Wait for the trademarks list to refetch before navigating
            await refetchTrademarks();
            selectTrademarkAndRedirect(response);
            toasts.showSuccessToast(
                <Toast text={translate("monitor_and_protect.features.trademark_config_create.success_toast")} />,
            );
        } catch {
            toasts.showErrorToast(
                <Toast
                    type="error"
                    text={translate("monitor_and_protect.features.trademark_config_create.error_toast")}
                />,
            );
        }
    };

    const handleCancelCreate = () => {
        void mnpTrademarkNavigation.trademarks.go();
    };

    return (
        <Stack
            paddingX="80px"
            paddingY="32px"
            gap="24px"
            alignItems="center"
        >
            <Widget width="796px">
                <Stack
                    padding="24px 24px 0px"
                    gap="8px"
                >
                    <Text fontVariant="h4">
                        {translate("monitor_and_protect.features.trademark_config_create.title")}
                    </Text>
                    {showAlert ? (
                        <Alert
                            type="info"
                            text={
                                <Text fontVariant="body2">
                                    {translate("monitor_and_protect.features.trademark_config_create.alert_text")}
                                </Text>
                            }
                            icon={<TargetIcon style={{ flexShrink: 0 }} />}
                            onClose={() => handleCloseTrademarkInfo()}
                        />
                    ) : null}
                </Stack>
                <TrademarkConfigsForm
                    isNew
                    onSave={(data) => void handleSaveConfig(data)}
                    isLoading={isPending || isFetching}
                    onCancel={handleCancelCreate}
                    confirmButtonText={translate(
                        "monitor_and_protect.features.trademark_config_create.add_trademark_button",
                    )}
                />
            </Widget>
        </Stack>
    );
};
